import { useState } from 'react'
import { useT } from '@/i18n'
import {
  DISPLAY_UTC_OFFSET_HOURS, trendColumns, trendStats, trendWinners, TREND_KINDS, type DrawnRound, type TrendKind,
} from '@/lib/keno-view'
import { Popper } from './Popper'
import type { Product } from './GameLot'

/** "10/20/2026 03:37:31 AM" — the reference draw-time format, in Thai time (the reference shows its server GMT-4). */
export function referenceTime(iso: string): string {
  const d = new Date(new Date(iso).getTime() + DISPLAY_UTC_OFFSET_HOURS * 3600_000)
  const p = (n: number) => String(n).padStart(2, '0')
  const h = d.getUTCHours() % 12 || 12
  return `${p(d.getUTCMonth() + 1)}/${p(d.getUTCDate())}/${d.getUTCFullYear()} ${p(h)}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())} ${d.getUTCHours() < 12 ? 'AM' : 'PM'}`
}

/** The roadmap under a table card — the reference TrendLot. */
export function TrendLot({
  product, rounds, kind, onKind,
}: {
  product: Product
  rounds: DrawnRound[] | null
  kind: TrendKind
  onKind: (k: TrendKind) => void
}) {
  const t = useT()
  const [openId, setOpenId] = useState<number | null>(null)
  const cols = rounds ? trendColumns(kind, rounds) : []

  return (
    <div>
      <div className="trendlot-types grid-x small-up-6">
        {TREND_KINDS.map((k) => (
          <div className="cell" key={k}>
            <span title={t(k)} className={kind === k ? 'selected' : ''} onClick={() => { setOpenId(null); onKind(k) }}>{t(`${k}_trend`)}</span>
          </div>
        ))}
      </div>{' '}
      {!rounds?.length && <div className="loading"><div className="loading-icon" /></div>}{' '}
      {rounds && rounds.length > 0 && (
        <div className="stats">
          {trendStats(kind, rounds).map((s) => (
            <span key={s.key}>
              {t(s.key)}{' '}
              <span>{s.percent}%</span>
            </span>
          ))}
        </div>
      )}{' '}
      <div className="grid-trendlot">
        {cols.map((col) => (
          <div className="rol" key={col[0]!.roundNumber}>
            {col.map((c) => {
              const r = c.round
              const title = `${t('DRAW_NO')} ${product.gCode}${r.roundNumber}\n${referenceTime(r.resultAt)}\nSum: ${r.draw.sum}`
              const chip = (onClick: () => void) => (
                <div onClick={onClick} className={`type ${c.type}`} title={title}>
                  {t(`${c.type}_trend`)}
                </div>
              )
              return (
                <div className={c.type} key={r.roundNumber}>
                  {openId !== r.roundNumber ? chip(() => setOpenId(r.roundNumber)) : (
                    <Popper
                      boundaryId={`gamecont-${product.gameId}`}
                      onDocumentClick={() => setOpenId(null)}
                      extraClass="trend"
                      reference={chip(() => setOpenId(null))}
                    >
                      <a className="close" onClick={() => setOpenId(null)} />{' '}
                      <div style={{ display: 'inline-block', width: '100%' }}>
                        <div className="gametitle">{product.title}</div>{' '}
                        <div className="drawno">{t('DRAW_NO')} {product.gCode}{r.roundNumber}</div>{' '}
                        <div className="drawtime">{referenceTime(r.resultAt)}</div>
                      </div>{' '}
                      <span className="sum">{r.draw.sum}</span>{' '}
                      <div className="trendsnumber">
                        {sortedColumns(r.draw.numbers).map(([a, b], i) => (
                          <div className="tilecolumn" key={i}>
                            <div className="tile"><span className={a >= 41 ? 'lighter' : ''}>{a}</span></div>{' '}
                            <div className="tile"><span className={b >= 41 ? 'lighter' : ''}>{b}</span></div>
                          </div>
                        ))}
                      </div>{' '}
                      <div style={{ textAlign: 'center' }}>
                        {trendWinners(r.draw).map((w) => (
                          <span className={`trendstat ${w}`} key={w}><span>{t(w)} </span></span>
                        ))}
                      </div>
                    </Popper>
                  )}
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}

/** The popover lists the 20 in ascending order, as the reference roadmap rows arrive. */
function sortedColumns(numbers: number[]): [number, number][] {
  const s = numbers.slice().sort((a, b) => a - b)
  return Array.from({ length: 10 }, (_, i) => [s[i]!, s[10 + i]!])
}
